import React, { useRef, useCallback } from 'react';

export type HeroTransform = {
  x: number;     // focal point 0-100
  y: number;     // focal point 0-100
  scale: number; // zoom 1.0-4.0
};

export const parseHeroTransform = (val?: string): HeroTransform => {
  const parts = (val || '').split(' ').map(Number);
  return {
    x: isFinite(parts[0]) ? parts[0] : 50,
    y: isFinite(parts[1]) ? parts[1] : 50,
    scale: isFinite(parts[2]) && parts[2] > 0 ? parts[2] : 1,
  };
};

export const serializeHeroTransform = (t: HeroTransform): string =>
  `${Math.round(t.x)} ${Math.round(t.y)} ${t.scale.toFixed(2)}`;

type Props = {
  imageUrl: string;
  value: string; // "x y scale" e.g. "40 20 1.50"
  onChange: (val: string) => void;
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const HeroImageEditor = ({ imageUrl, value, onChange }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const t = parseHeroTransform(value);
  const isDefault = t.x === 50 && t.y === 50 && t.scale === 1;

  const update = useCallback(
    (updates: Partial<HeroTransform>) => {
      const next = { ...parseHeroTransform(value), ...updates };
      next.x = clamp(next.x, 0, 100);
      next.y = clamp(next.y, 0, 100);
      next.scale = clamp(parseFloat(next.scale.toFixed(2)), 0.3, 4);
      onChange(serializeHeroTransform(next));
    },
    [value, onChange]
  );

  // Drag → pan
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      // Drag right → image moves right → focal point shifts left (subtract)
      const dx = (-e.movementX / (width * t.scale)) * 100;
      const dy = (-e.movementY / (height * t.scale)) * 100;
      update({ x: t.x + dx, y: t.y + dy });
    },
    [t, update]
  );

  const stopDrag = useCallback(() => {
    isDragging.current = false;
  }, []);

  return (
    <div className="col-span-6">
      <label className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2 mb-1">
        Hero Image Position
      </label>
      <p className="text-xs text-gray-500 mb-3">
        Use the <strong>slider</strong> to zoom · <strong>Drag</strong> to reposition ·
        Preview matches the crop shown on the blog post page.
      </p>

      {/* Live preview — matches 16:6 customer hero */}
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-xl border-2 border-gray-200 select-none"
        style={{ aspectRatio: '16 / 6', cursor: t.scale > 1 ? 'grab' : 'default' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
      >
        <img
          src={imageUrl}
          alt="Hero preview"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{
            transform: `scale(${t.scale})`,
            transformOrigin: `${t.x}% ${t.y}%`,
          }}
          draggable={false}
        />
        {/* Gradient overlay — same as customer site */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent pointer-events-none" />

        {/* Zoom badge */}
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full pointer-events-none font-mono">
          {Math.round(t.scale * 100)}%
        </div>

        {t.scale === 1 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="bg-black/50 text-white text-xs px-3 py-1.5 rounded-full">
              Scroll to zoom in, then drag to reposition
            </span>
          </div>
        )}
      </div>

      {/* Zoom slider */}
      <div className="flex items-center gap-3 mt-3">
        <span className="text-xs text-gray-500 w-8">Zoom</span>
        <input
          type="range"
          min={30}
          max={400}
          step={5}
          value={Math.round(t.scale * 100)}
          onChange={(e) => update({ scale: Number(e.target.value) / 100 })}
          className="flex-1 accent-primary-500"
        />
        <span className="text-xs text-gray-500 w-10 text-right font-mono">
          {Math.round(t.scale * 100)}%
        </span>
        {!isDefault && (
          <button
            type="button"
            onClick={() => onChange('50 50 1.00')}
            className="text-xs text-gray-400 hover:text-gray-600 underline whitespace-nowrap"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default HeroImageEditor;
